import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  PAYMENT_PAGE_PAYMENT_PENDING,
  PAYMENT_PAGE_PAYMENT_ERROR,
  PAYMENT_PAGE_TRANSACTION_NOT_FOUND,
  PAYMENT_PAGE_PAYMENT_SUCCESS,
} from "../../constants/text_constants";
import LoadingLottie from "../../assets/Loadin_animation.json";
import CustomLottie from "../Lottie-Components/CustomLottie";
import ErrorLottie from "../../assets/Error_animation.json";
import NotFoundLottie from "../../assets/NotFoundLottie.json";
import SuccessLottie from "../../assets/SuccessLottie.json";

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const transactionId = searchParams.get("transaction_id");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [text, setText] = useState("");
  const [orderPlaced, setOrderPlaced] = useState(false);
  const cartData = JSON.parse(sessionStorage.getItem(transactionId));
  const placeOrder = async (transId, retries = 6) => {
    // Prevent duplicate orders
    if (orderPlaced) {
      console.log("Order already placed, skipping duplicate order");
      return;
    }

    try {
      const authToken = sessionStorage.getItem("token");

      const requestOptions = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      
      const response = await fetch(
        process.env.REACT_APP_RANK_PLUS_PLACE_ORDER + `?item_type=${cartData?.item_type}&item_id=${cartData?.item_id}&suggested_book=&total_section_division=${cartData?.total_section_division}&used_wallet=${cartData?.used_wallet}&offer_code=${cartData?.offer_code}&referral_code=${cartData?.referral_code}&grand_total=${cartData?.grand_total}&order_status=PAID&transaction_id=${transId}&transaction_amount=${cartData?.transaction_amount}&test_package_id=0&gatewayType=${cartData?.gatewayType}`,
        requestOptions
      );

      if (!response.ok) {
        if (response.status >= 500 && response.status < 600 && retries > 0) {
          console.warn(`Retrying... attempts left: ${retries}`);
          setTimeout(() => placeOrder(transId, retries - 1), 10000);
          return;
        } else {
          throw new Error(`HTTP error: ${response.status}`);
        }
      }
      const orderData = await response.json();
      // Mark order as placed to prevent duplicates
      if( orderData?.status && orderData.data?.order_id ) {
        setOrderPlaced(true);
        // Store in sessionStorage to persist across refreshes
        sessionStorage.setItem(`order_placed_${transId}`, 'true');
      }
    } catch (err) {
      console.error("Error placeOrder", err);
    }
  };

  const fetchTransactionStatus = async (retries = 6) => {
    try {
      const authToken = sessionStorage.getItem("token");
      const requestOptions = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      const response = await fetch(
        process.env.REACT_APP_RANK_PLUS_TRANSACTION_STATUS + transactionId,
        requestOptions
      );

      if (!response.ok) {
        if (response.status >= 500 && response.status < 600 && retries > 0) {
          console.warn(`Retrying... attempts left: ${retries}`);
          setTimeout(() => fetchTransactionStatus(retries - 1), 10000);
          return;
        } else {
          throw new Error(`HTTP error: ${response.status}`);
        }
      }
      const json = await response.json();
      setPaymentStatus(json?.code);
      if (json?.code === PAYMENT_PAGE_PAYMENT_PENDING && retries > 0) {
        setTimeout(() => fetchTransactionStatus(retries - 1), 10000);
        setText("Your Payment is still pending");
      }
      if (json?.code === PAYMENT_PAGE_PAYMENT_PENDING && retries === 0) {
        setText(
          "You payment is still pending. It is taking a little more time than expected. If payment has been deducted from your account and product hasn't been unlocked. Please contact rankplus support "
        );
      }
      if (json?.code === PAYMENT_PAGE_PAYMENT_ERROR) {
        setText("Payment failed. Please try after some time.");
      }
      if (json?.code === PAYMENT_PAGE_TRANSACTION_NOT_FOUND) {
        setText("The given transaction is not valid !!!");
      }
      if (json?.code === PAYMENT_PAGE_PAYMENT_SUCCESS) {
        setText("Transaction Complete !!!");
        // Check if order already placed before calling placeOrder
        if (!sessionStorage.getItem(`order_placed_${json?.data?.transactionId}`)) {
          placeOrder(json?.data?.transactionId);
          // Set timer to redirect to home page after 5 seconds
          setTimeout(() => {
            navigate("/");
          }, 6000);
        }
      }
    } catch (err) {
      console.error("Error fetchTransactionStatus", err);
    }
  };

  const fetchTransactionStatusRazorpay = async (retries = 6) => {
    try {
      const authToken = sessionStorage.getItem("token");
      const requestOptions = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };

      const response = await fetch(
        process.env.REACT_APP_RANK_PLUS_TRANSACTION_STATUS_RAZORPAY + cartData?.razorpay_order_id,
        requestOptions
      );

      if (!response.ok) {
        if (response.status >= 500 && response.status < 600 && retries > 0) {
          console.warn(`Retrying... attempts left: ${retries}`);
          setTimeout(() => fetchTransactionStatusRazorpay(retries - 1), 10000);
          return;
        } else {
          throw new Error(`HTTP error: ${response.status}`);
        }
      }
      const json = await response.json();
      if(json?.status === "paid") {
        setPaymentStatus("PAYMENT_SUCCESS");
        setText("Transaction Complete !!!");
        // Check if order already placed before calling placeOrder
        if (!sessionStorage.getItem(`order_placed_${transactionId}`)) {
          placeOrder(transactionId);
          // Set timer to redirect to home page after 5 seconds
          setTimeout(() => {
            navigate("/");
          }, 6000);
        }
      } else {
        setPaymentStatus("PAYMENT_ERROR");
        setText("Payment failed. Please try after some time.");
      }
    
    } catch (err) {
      console.error("Error fetchTransactionStatusRazorpay", err);
    }
  };

  useEffect(() => {  
    // Check if order was already placed on page load
    if (sessionStorage.getItem(`order_placed_${transactionId}`)) {
      setOrderPlaced(true);
      setPaymentStatus("PAYMENT_SUCCESS");
      setText("Transaction Complete !!!");
      return;
    }
    
    if (cartData?.gatewayType === "2") {
      fetchTransactionStatusRazorpay();
    } else {
      fetchTransactionStatus();
    }
  }, []);

  return (
    <div className="flex justify-center items-center -ml-4 -mb-[2rem]">
      <div className="w-full max-w-md">
        {paymentStatus === PAYMENT_PAGE_PAYMENT_PENDING && (
          <CustomLottie animationData={LoadingLottie} text={text} />
        )}
        {paymentStatus === PAYMENT_PAGE_PAYMENT_ERROR && (
          <CustomLottie animationData={ErrorLottie} text={text} />
        )}
        {paymentStatus === PAYMENT_PAGE_TRANSACTION_NOT_FOUND && (
          <CustomLottie animationData={NotFoundLottie} text={text} />
        )}
        {paymentStatus === PAYMENT_PAGE_PAYMENT_SUCCESS && (
          <CustomLottie animationData={SuccessLottie} text={text} />
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
